import { forwardRef, useMemo, useState } from 'react'

import { useApolloClient } from '@apollo/client'
import { Skeleton } from '@factorialco/deprecated-design-system'
import { F0Alert, F0Box, F0Dialog, F0Text, IconType } from '@factorialco/f0-react'
import { EntityId, EntitySelectEntity, NumberInput } from '@factorialco/f0-react/dist/experimental'

import { AlertCircle, CheckCircle, Warning } from '@factorialco/f0-react/icons/app'
import {
  AddGroupModalTrainingsWithClassesDocument,
  AssignBudgetToTrainingClassDocument,
} from 'generated/resources/graphql'

import { EntitySelectWrapper } from 'modules/core/components/EntitySelectWrapper'
import { flatSelection } from 'modules/core/components/EntitySelectWrapper/lib/flat'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import { amountWithCurrency } from 'modules/finance/lib/budgets'

import {
  calculateAllocationCents,
  getSelectedEntities,
  mapTrainingsToEntities,
} from 'modules/finance/components/TrainingsBudgetWizard/AllocationStep/lib/trainingEntityMapper'

interface Props {
  budgetId: number
  currency: string
  availableAmountCents: number
  locale: string
  onClose: () => void
}

type BudgetStatus = 'normal' | 'at-risk' | 'over-budget'

const AT_RISK_THRESHOLD_PERCENT = 10

const getBudgetStatus = (availableCents: number, totalAvailableCents: number): BudgetStatus => {
  if (availableCents < 0) return 'over-budget'
  const atRiskThreshold = totalAvailableCents * (AT_RISK_THRESHOLD_PERCENT / 100)
  if (availableCents <= atRiskThreshold) return 'at-risk'
  return 'normal'
}

// eslint-disable-next-line no-type-assertion/no-type-assertion
const GreenCheckCircle = forwardRef<SVGSVGElement, React.SVGProps<SVGSVGElement>>((props, ref) => (
  <CheckCircle {...props} ref={ref} style={{ ...props.style, color: 'hsl(var(--positive-60))' }} />
)) as IconType

// eslint-disable-next-line no-type-assertion/no-type-assertion
const OrangeWarning = forwardRef<SVGSVGElement, React.SVGProps<SVGSVGElement>>((props, ref) => (
  <Warning {...props} ref={ref} style={{ ...props.style, color: 'hsl(var(--warning-60))' }} />
)) as IconType

// eslint-disable-next-line no-type-assertion/no-type-assertion
const RedAlertCircle = forwardRef<SVGSVGElement, React.SVGProps<SVGSVGElement>>((props, ref) => (
  <AlertCircle {...props} ref={ref} style={{ ...props.style, color: 'hsl(var(--critical-60))' }} />
)) as IconType

const budgetStatusIcon: Record<BudgetStatus, IconType> = {
  normal: GreenCheckCircle,
  'at-risk': OrangeWarning,
  'over-budget': RedAlertCircle,
}

export const AddGroupModal = ({
  budgetId,
  currency,
  availableAmountCents,
  locale,
  onClose,
}: Props) => {
  const apolloClient = useApolloClient()
  const [selectedClassIds, setSelectedClassIds] = useState<string[]>([])
  const [expandedElements, setExpandedElements] = useState<EntityId[]>([])
  const [submitting, setSubmitting] = useState(false)

  const { data: trainingsData, loading } = useQuery(AddGroupModalTrainingsWithClassesDocument)

  const trainings = useMemo(() => {
    const allTrainings =
      trainingsData?.trainings?.trainingsConnection?.nodes?.filter(
        (node): node is NonNullable<typeof node> => node !== null
      ) ?? []

    return allTrainings
      .map((training) => ({
        ...training,
        trainingClassesConnection: {
          ...training.trainingClassesConnection,
          nodes: training.trainingClassesConnection.nodes.filter((cls) => !cls.budgetMovement),
        },
      }))
      .filter((training) => training.trainingClassesConnection.nodes.length > 0)
  }, [trainingsData])

  const entities = useMemo(
    () => mapTrainingsToEntities(trainings, expandedElements),
    [trainings, expandedElements]
  )

  const selectedEntities = useMemo(
    () => getSelectedEntities(entities, selectedClassIds),
    [entities, selectedClassIds]
  )

  const selectedCostCents = useMemo(
    () => calculateAllocationCents(trainings, selectedClassIds),
    [trainings, selectedClassIds]
  )

  const newBalanceCents = availableAmountCents - selectedCostCents
  const budgetStatus = getBudgetStatus(newBalanceCents, availableAmountCents)
  const remainingPercentage =
    availableAmountCents > 0 ? Math.round((newBalanceCents / availableAmountCents) * 100) : 0

  const [assignBudgetToTrainingClass] = useMutation(AssignBudgetToTrainingClassDocument, {
    extractorFn: (data) => data.trainingsMutations.updateTrainingsTrainingClass,
  })

  const handleSelectMultiple = (selection: EntitySelectEntity[] | null) => {
    if (!selection) {
      setSelectedClassIds([])
      return
    }
    const classIds = flatSelection(selection).map((item) => String(item.subId))
    setSelectedClassIds(classIds)
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    try {
      await Promise.all(
        selectedClassIds.map((classId) =>
          assignBudgetToTrainingClass({
            variables: { id: Number(classId), budgetId },
          })
        )
      )
      await apolloClient.refetchQueries({
        include: [
          'FinanceBudgetDetailTrainingResourceDataCollection',
          'FinanceBudgetDetailRevampPage',
          'FinanceBudgetDetailAmountWidgets',
        ],
      })
      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <F0Dialog
      isOpen
      onClose={onClose}
      title={i18n.t('budgets.add_group_modal.title')}
      width="xl"
      primaryAction={{
        label: i18n.t('budgets.add_group_modal.add_button'),
        onClick: handleSubmit,
        disabled: selectedClassIds.length === 0 || submitting,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      <F0Box display="flex" flexDirection="column" gap="xl">
        {loading ? (
          <F0Box flexDirection="column" gap="md">
            <Skeleton height="s64" width="full" />
            <Skeleton height="s64" width="full" />
          </F0Box>
        ) : (
          <EntitySelectWrapper
            size="md"
            entities={entities}
            groups={[{ label: i18n.t('common.all'), value: 'all' }]}
            expandedElements={expandedElements}
            selectedGroup="all"
            selectedGroupedEntities={selectedEntities}
            loading={loading}
            multiple
            hiddenAvatar
            singleSelector={false}
            label={i18n.t('budgets.add_group_modal.select_label')}
            placeholder={i18n.t('budgets.add_group_modal.select_placeholder')}
            searchPlaceholder={i18n.t('common.search')}
            selectAllLabel={i18n.t('common.select_all')}
            clearLabel={i18n.t('common.clear')}
            selectedLabel={i18n.t('common.selected')}
            selectedItemsCopy={i18n.t(
              'budgets.forms.trainings_budget.allocation.trainings_selected'
            )}
            notFoundTitle={i18n.t('budgets.forms.trainings_budget.allocation.no_trainings')}
            notFoundSubtitle=""
            onSelect={() => {}}
            onSelectMultiple={handleSelectMultiple}
            onGroupChange={() => {}}
            onSelectExpandedElements={setExpandedElements}
            required
          />
        )}

        {/* Budget impact summary */}
        <F0Box
          display="flex"
          border="default"
          borderRadius="md"
          borderColor="secondary"
          padding="md"
          flexDirection="column"
          gap="md"
        >
          <F0Text
            variant="label"
            content={i18n.t('budgets.add_group_modal.budget_impact_summary')}
          />
          <F0Box display="flex" flexDirection="row" gap="lg" width="full">
            <F0Box width="full">
              <NumberInput
                disabled
                label={i18n.t('budgets.add_group_modal.available')}
                locale={i18n.locale}
                units={currency}
                value={availableAmountCents / 100}
                maxDecimals={2}
                size="md"
              />
            </F0Box>
            <F0Box width="full">
              <NumberInput
                disabled
                label={i18n.t('budgets.add_group_modal.selected_cost')}
                locale={i18n.locale}
                units={currency}
                value={selectedCostCents / 100}
                maxDecimals={2}
                size="md"
              />
            </F0Box>
            <F0Box width="full">
              <NumberInput
                disabled
                label={i18n.t('budgets.add_group_modal.new_balance')}
                locale={i18n.locale}
                units={currency}
                value={newBalanceCents / 100}
                maxDecimals={2}
                size="md"
                icon={selectedClassIds.length > 0 ? budgetStatusIcon[budgetStatus] : undefined}
                status={
                  selectedClassIds.length > 0 && budgetStatus === 'over-budget'
                    ? { type: 'error' as const }
                    : undefined
                }
              />
            </F0Box>
          </F0Box>
        </F0Box>

        {/* Status banners */}
        {selectedClassIds.length > 0 && budgetStatus === 'normal' && (
          <F0Alert
            description=""
            variant="positive"
            title={i18n.t('budgets.add_group_modal.within_budget', {
              percentage: remainingPercentage,
            })}
          />
        )}
        {selectedClassIds.length > 0 && budgetStatus === 'at-risk' && (
          <F0Alert
            description=""
            variant="warning"
            title={i18n.t('budgets.add_group_modal.budget_risk', {
              percentage: AT_RISK_THRESHOLD_PERCENT,
            })}
          />
        )}
        {selectedClassIds.length > 0 && budgetStatus === 'over-budget' && (
          <F0Alert
            description=""
            variant="critical"
            title={i18n.t('budgets.add_group_modal.over_budget', {
              amount: amountWithCurrency(Math.abs(newBalanceCents), currency, locale),
            })}
          />
        )}
      </F0Box>
    </F0Dialog>
  )
}
